import { useState } from 'react';
import {
  useAdminMaterials,
  useUpdateMaterialStock,
  useCreateMaterial,
  useUpdateMaterial,
  useDeleteMaterial,
} from '@/hooks/useAdmin';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { MaterialForm } from '@/components/admin/MaterialForm';
import { MaterialManageCard } from '@/components/admin/MaterialManageCard';
import type { CreateMaterialInput, UpdateMaterialInput } from '@/services/admin';

export const AdminMaterialsPage = () => {
  const { data: materials, isLoading } = useAdminMaterials();
  const updateStock = useUpdateMaterialStock();
  const createMaterial = useCreateMaterial();
  const updateMaterial = useUpdateMaterial();
  const deleteMaterial = useDeleteMaterial();

  const [showForm, setShowForm] = useState(false);
  const toast = useToast();

  const handleCreate = (input: CreateMaterialInput) => {
    createMaterial.mutate(input, {
      onSuccess: () => {
        setShowForm(false);
        toast.success('Material created');
      },
      onError: () => toast.error('Failed to create material'),
    });
  };

  const handleUpdate = (materialId: string, updates: UpdateMaterialInput) => {
    updateMaterial.mutate(
      { materialId, updates },
      {
        onSuccess: () => toast.success('Material updated'),
        onError: () => toast.error('Failed to update material'),
      },
    );
  };

  const handleToggleStock = (materialId: string, updates: { colorStock?: Record<string, boolean> }) => {
    updateStock.mutate(
      { materialId, updates },
      { onError: () => toast.error('Failed to update stock') },
    );
  };

  const handleDelete = (materialId: string) => {
    deleteMaterial.mutate(materialId, {
      onSuccess: () => toast.success('Material deleted'),
      onError: () => toast.error('Failed to delete material'),
    });
  };

  const isAnyMutating = updateStock.isPending || createMaterial.isPending || updateMaterial.isPending || deleteMaterial.isPending;

  return (
    <div>
      <div className="admin-page-header" style={{ marginBottom: 'var(--space-6)' }}>
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Materials
          </h1>
          <p
            style={{
              marginTop: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-tertiary)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            Create, edit, and manage materials and their color options. Out-of-stock colors are hidden from customers.
          </p>
        </div>
        {!showForm && (
          <Button className="responsive-button" onClick={() => setShowForm(true)} disabled={isAnyMutating}>
            Add Material
          </Button>
        )}
      </div>

      {/* Create Form */}
      {showForm && (
        <MaterialForm
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          isPending={createMaterial.isPending}
        />
      )}

      {isLoading && (
        <div style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-20)' }}>
          <LoadingSpinner size="lg" />
        </div>
      )}

      {materials && materials.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {materials.map((material) => (
            <MaterialManageCard
              key={material.materialId}
              material={material}
              onUpdate={handleUpdate}
              onToggleStock={handleToggleStock}
              onDelete={handleDelete}
              isUpdating={isAnyMutating}
            />
          ))}
        </div>
      )}

      {!isLoading && materials && materials.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--space-16)',
            color: 'var(--text-tertiary)',
          }}
        >
          <p style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>
            No materials yet
          </p>
          <p style={{ fontSize: 'var(--text-sm)' }}>
            Click "Add Material" to create your first material.
          </p>
        </div>
      )}
    </div>
  );
};
